import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { AuthProvider, useAuth } from './context/AuthContext';
import theme from './theme/theme';
import Navbar from './components/Layout/Navbar';
import Sidebar from './components/Layout/Sidebar';
import Login from './pages/Auth/Login';
import Dashboard from './pages/Dashboard/Dashboard';
import Organisations from './pages/Organisations/Organisations';
import OrganisationDetail from './pages/Organisations/OrganisationDetail';
import GroupDetail from './pages/Groups/GroupDetail';
import Settings from './pages/Settings/Settings';
import Logs from './pages/Logs/Logs';
import WikiWasteSearch from './pages/WikiWasteSearch/WikiWasteSearch';
import Administrators from './pages/Administrators/Administrators';
import GroupAdministrators from './pages/GroupAdministrators/GroupAdministrators';
import LiveAdverts from './pages/LiveAdverts/LiveAdverts';
import AdvertLibrary from './pages/AdvertLibrary/AdvertLibrary';
import Events from './pages/Events/Events';
import GroupLogs from './pages/GroupLogs/GroupLogs';
import FullPageLoader from './components/Common/FullPageLoader';

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <FullPageLoader />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  return children;
};

const AppContent = () => {
  const { isAuthenticated } = useAuth();
  
  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <Navbar />
      <Sidebar />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          mt: 8,
          // ml: { sm: '280px' },
          width: { sm: `calc(100% - 280px)` },
          minHeight: '100vh',
          backgroundColor: '#f5f7fa'
        }}
      >
        <Routes>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/organisations" element={<Organisations />} />
          <Route path="/organisations/:id" element={<OrganisationDetail />} />
          <Route path="/groups/:id" element={<GroupDetail />} />
          
          {/* Settings with Administrators submenu */}
          <Route path="/settings" element={<Settings />} />
          <Route path="/administrators" element={<Administrators />} />
          
          {/* Main menu items */}
          <Route path="/logs" element={<Logs />} />
          <Route path="/wikiwaste-search" element={<WikiWasteSearch />} />
          
          {/* Group submenu items */}
          <Route path="/group-administrators/:groupId" element={<GroupAdministrators />} />
          <Route path="/live-adverts/:groupId" element={<LiveAdverts />} />
          <Route path="/advert-library/:groupId" element={<AdvertLibrary />} />
          <Route path="/events/:groupId" element={<Events />} />
          <Route path="/group-logs/:groupId" element={<GroupLogs />} />
          
          <Route path="/" element={<Navigate to="/dashboard" />} />
          <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
      </Box>
    </Box>
  );
};

const App = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
};

export default App;